// src/forms/localMachine/moveMachineBetweenDepositosForm.js
/* eslint-disable */

import React, { Component } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Container,
  Paper,
  Typography,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import esLocale from "date-fns/locale/es";

import MachineService from "services/machineService";
import CancelButton from "components/common/CancelButton";
import AcceptButton from "components/common/AcceptButton";
import {
  dateOnlyFromDateTime,
  getDefaultOperationDate,
  isBeforeMinOperationDate,
  isFutureOperationDate,
  isValidOperationDate,
  parseDateOnlyToLocalDate,
  toLocalDateOnlyString,
} from "utils/operationDate";

class MoveMachineBetweenDepositosForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedDestinoId: "",
      submitting: false,
      effectiveDate: getDefaultOperationDate(),
      generalError: "",
      showDestinoError: false,
    };
  }

  handleChangeDestino = (event) => {
    const value = event.target.value;
    this.setState({
      selectedDestinoId: Number(value),
      generalError: "",
      showDestinoError: false,
    });
  };

  handleDateChange = (date) => {
    this.setState({ effectiveDate: date, generalError: "" });
  };

  getSelectedDestino = () =>
    (this.props.depositos || []).find(
      (deposito) => Number(deposito?.Id) === Number(this.state.selectedDestinoId)
    ) || null;

  getMinEffectiveDate = () => {
    const machineStartDate = parseDateOnlyToLocalDate(
      dateOnlyFromDateTime(this.props.machine?.StartDateTime)
    );
    const destinoCreatedAtDate = parseDateOnlyToLocalDate(
      dateOnlyFromDateTime(this.getSelectedDestino()?.CreatedAt)
    );

    if (!machineStartDate) return destinoCreatedAtDate;
    if (!destinoCreatedAtDate) return machineStartDate;

    return machineStartDate.getTime() >= destinoCreatedAtDate.getTime()
      ? machineStartDate
      : destinoCreatedAtDate;
  };

  getEffectiveDateErrorMessage = () => {
    const { effectiveDate } = this.state;
    const minDate = this.getMinEffectiveDate();
    const hasDestinoCreatedAt = Boolean(this.getSelectedDestino()?.CreatedAt);

    if (!isValidOperationDate(effectiveDate)) {
      return "Selecciona una fecha válida.";
    }
    if (isFutureOperationDate(effectiveDate)) {
      return "No se puede seleccionar una fecha futura.";
    }
    if (minDate && isBeforeMinOperationDate(effectiveDate, minDate)) {
      if (hasDestinoCreatedAt) {
        return "La fecha seleccionada no puede ser anterior al último movimiento de la máquina ni a la fecha de creación del depósito destino.";
      }
      return "La fecha seleccionada no puede ser anterior al último movimiento de la máquina.";
    }
    return "";
  };

  getApiErrorMessage = (result) => {
    if (result?.status === 409 && result?.code === "EFFECTIVE_DATE_BEFORE_MACHINE_CREATION") {
      return "La fecha seleccionada no puede ser anterior a la fecha de creación de la máquina.";
    }
    if (
      result?.status === 409 &&
      result?.code === "EFFECTIVE_DATE_BEFORE_CURRENT_LOCAL_MACHINE_START"
    ) {
      return "La fecha seleccionada no puede ser anterior al último movimiento de la máquina.";
    }
    if (
      result?.status === 409 &&
      result?.code === "EFFECTIVE_DATE_BEFORE_TARGET_LOCAL_CREATION"
    ) {
      return "La fecha seleccionada no puede ser anterior a la fecha de creación del depósito destino.";
    }
    if (result?.status === 409 && result?.code === "TARGET_LOCAL_NOT_ACTIVE") {
      return "El depósito destino debe estar activo.";
    }
    if (result?.status === 400 && result?.code === "EFFECTIVE_DATE_CANNOT_BE_FUTURE") {
      return "No se puede seleccionar una fecha futura.";
    }

    return result?.message || "No se pudo mover la máquina.";
  };

  handleSubmit = async (event) => {
    event.preventDefault();
    const { depositoLocal, machine, onMoveSuccess } = this.props;
    const { selectedDestinoId, effectiveDate } = this.state;

    if (!selectedDestinoId) {
      this.setState({ showDestinoError: true, generalError: "" });
      return;
    }

    const effectiveDateError = this.getEffectiveDateErrorMessage();
    if (effectiveDateError) {
      return;
    }

    const fromLocalId = Number(depositoLocal.Id);
    const toLocalId = Number(selectedDestinoId);

    if (!Number.isInteger(fromLocalId) || !Number.isInteger(toLocalId)) {
      this.setState({ generalError: "IDs de depósito inválidos." });
      return;
    }

    this.setState({ submitting: true, generalError: "" });

    try {
      const result = await MachineService.moveMachineBetweenDepositos(
        machine.Id,
        fromLocalId,
        toLocalId,
        toLocalDateOnlyString(effectiveDate)
      );

      if (result?.ok) {
        if (typeof onMoveSuccess === "function") onMoveSuccess();
      } else {
        this.setState({
          generalError: this.getApiErrorMessage(result),
        });
      }
    } catch (error) {
      console.error("Error al mover la máquina:", error);
      this.setState({
        generalError: "Error al mover la máquina.",
      });
    } finally {
      this.setState({ submitting: false });
    }
  };

  render() {
    const { depositoLocal, depositos, closeForm } = this.props;
    const { selectedDestinoId, submitting, effectiveDate, generalError } = this.state;
    const minEffectiveDate = this.getMinEffectiveDate();
    const effectiveDateError = this.getEffectiveDateErrorMessage();
    const today = getDefaultOperationDate();

    return (
      <Container component={Paper} style={{ padding: "20px", marginTop: "20px" }}>
        <form onSubmit={this.handleSubmit} noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                Actualmente en: {depositoLocal?.Name || "sin nombre"}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required error={this.state.showDestinoError && !selectedDestinoId}>
                <InputLabel>Depósito destino</InputLabel>
                <Select
                  value={selectedDestinoId}
                  onChange={this.handleChangeDestino}
                  label="Depósito destino"
                >
                  {depositos.map((d) => (
                    <MenuItem key={d.Id} value={d.Id}>
                      {d.Name || "Sin nombre"}
                      {d.City ? ` · ${d.City}` : ""}
                      {d.Department ? ` · ${d.Department}` : ""}
                    </MenuItem>
                  ))}
                </Select>
                {this.state.showDestinoError && !selectedDestinoId && <FormHelperText>Seleccioná un depósito destino.</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
                <DatePicker
                  label="Fecha de movimiento"
                  value={effectiveDate}
                  onChange={this.handleDateChange}
                  minDate={minEffectiveDate || undefined}
                  maxDate={today}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: Boolean(effectiveDateError),
                      helperText: effectiveDateError,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            {generalError && (
              <Grid item xs={12}>
                <Typography variant="body2" color="error">
                  {generalError}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12} sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <CancelButton onClick={closeForm} disabled={submitting} fullWidth>
                    Cancelar
                  </CancelButton>
                </Grid>

                <Grid item xs={12} md={6}>
                  <AcceptButton
                    type="submit"
                    disabled={submitting || Boolean(effectiveDateError)}
                    fullWidth
                  >
                    {submitting ? "Moviendo..." : "Mover máquina"}
                  </AcceptButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </Container>
    );
  }
}

MoveMachineBetweenDepositosForm.propTypes = {
  closeForm: PropTypes.func.isRequired,
  depositoLocal: PropTypes.object.isRequired,
  machine: PropTypes.object.isRequired,
  depositos: PropTypes.array.isRequired,
  onMoveSuccess: PropTypes.func,
};

export default MoveMachineBetweenDepositosForm;
